import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  SunIcon,
  MoonIcon,
  LanguageIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import Sidebar from './SidebarNew';
import { selectUser, selectIsAuthenticated, logoutUser } from '../../store/slices/authSlice';
import { useGetMySubscriptionQuery } from '../../services/subscriptionApi';
import { NotificationBell } from '../NotificationBell';

const Layout = () => {
  const { t, i18n } = useTranslation(['common', 'navigation']);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Initialize sidebarOpen based on screen size: open on desktop (lg+), closed on mobile
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024; // lg breakpoint
    }
    return false; // Fallback for SSR
  });

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  // Notifications handled by NotificationBell
  const [darkMode, setDarkMode] = useState(false);

  // Get subscription data
  const { data: subscriptionData, isLoading: subscriptionLoading, error: subscriptionError } = useGetMySubscriptionQuery();

  // Handle window resize to update sidebar state for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      setSidebarOpen(isDesktop);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setUserMenuOpen(false);
      setLanguageMenuOpen(false);
    };

    if (userMenuOpen || languageMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [userMenuOpen, languageMenuOpen]);

  // Check authentication and redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Notification dropdown is powered by NotificationBell component

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, navigate to login
      navigate('/login');
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLanguageMenuOpen(false);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    // In real app, save to localStorage and apply dark mode classes
  };

  const currentLanguage = i18n.language;
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
  ];

  // Don't render layout if user is not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-mesh">
      {/* Sidebar */}
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen}
        currentPath={location.pathname}
        userRole={user?.role || 'employee'}
        // subscriptionData is wrapper { success, data }
        subscription={subscriptionData}
        subscriptionLoading={subscriptionLoading}
        subscriptionError={subscriptionError}
      />

      {/* Main Content */}
      <div className={`transition-all duration-500 ease-in-out ${sidebarOpen ? 'lg:ml-72' : 'lg:ml-20'}`}>
        {/* Modern Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-secondary-200 shadow-lg">
          <div className="px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16 sm:h-20">
              {/* Left Section */}
              <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
                {/* Mobile Menu Button */}
                <button
                  type="button"
                  className="lg:hidden -ml-0.5 -mt-0.5 h-10 w-10 sm:h-12 sm:w-12 inline-flex items-center justify-center rounded-lg sm:rounded-xl text-secondary-700 hover:text-secondary-900 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-all duration-200 hover:scale-105"
                  onClick={() => setSidebarOpen(true)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
                
                {/* Desktop Sidebar Toggle */}
                <button
                  type="button"
                  className="hidden lg:flex -ml-0.5 -mt-0.5 h-12 w-12 items-center justify-center rounded-xl text-secondary-700 hover:text-secondary-900 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-all duration-200 hover:scale-105"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <span className="sr-only">Toggle sidebar</span>
                  {sidebarOpen ? (
                    <XMarkIcon className="h-6 w-6" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" />
                  )}
                </button>

                {/* Breadcrumb Navigation - Hidden on mobile */}
                <div className="hidden xl:flex items-center space-x-2">
                  <div className="flex items-center space-x-2 text-secondary-700">
                    <HomeIcon className="h-4 w-4" />
                    <span className="text-sm">/</span>
                    <span className="text-sm font-medium text-secondary-900 capitalize">
                      {location.pathname.substring(1).replace('/', ' / ')}
                    </span>
                  </div>
                </div>

                {/* Page Title */}
                <div className="ml-2 sm:ml-4 min-w-0 flex-1">
                  <h1 className="text-lg sm:text-xl font-display font-bold text-secondary-900 tracking-tight truncate">
                    {t(`navigation:${location.pathname.substring(1)}`, location.pathname.substring(1))}
                  </h1>
                  <p className="text-xs sm:text-sm text-secondary-500 hidden md:block">
                    Manage your HR operations efficiently
                  </p>
                </div>
              </div>

              {/* Right Section */}
              <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3 flex-shrink-0">
                {/* Search Bar - Hidden on mobile */}
                {/* <div className="hidden lg:block relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-48 xl:w-64 pl-10 pr-4 py-2 border border-secondary-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 placeholder-secondary-500 text-secondary-900 text-sm"
                  />
                </div> */}

                {/* Notifications */}
                <NotificationBell />

                {/* Dark Mode Toggle - Hidden on mobile */}
                {/* <button
                  type="button"
                  className="hidden sm:block p-2 text-secondary-700 hover:text-secondary-900 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-xl transition-all duration-200 hover:scale-105"
                  onClick={toggleDarkMode}
                >
                  {darkMode ? (
                    <SunIcon className="h-6 w-6" />
                  ) : (
                    <MoonIcon className="h-6 w-6" />
                  )}
                </button> */}

                {/* Language Selector */}
                <div className="relative">
                  <button
                    type="button"
                    className="p-1.5 sm:p-2 text-secondary-700 hover:text-secondary-900 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg sm:rounded-xl transition-all duration-200 hover:scale-105"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLanguageMenuOpen(!languageMenuOpen);
                    }}
                  >
                    <LanguageIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>

                  {languageMenuOpen && (
                    <div className="absolute right-0 mt-3 w-56 sm:w-64 bg-white rounded-2xl shadow-hard ring-1 ring-secondary-900/5 py-2 z-50 animate-fade-in-up">
                      <div className="px-4 py-3 border-b border-secondary-100">
                        <h3 className="text-sm font-display font-semibold text-secondary-900">Language</h3>
                      </div>
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          className="flex items-center justify-between w-full px-4 py-3 text-sm text-secondary-700 hover:bg-secondary-50 transition-all duration-200 hover:translate-x-1"
                          onClick={() => changeLanguage(lang.code)}
                        >
                          <div className="flex items-center">
                            <span className="mr-3 text-lg">{lang.flag}</span>
                            <span>{lang.name}</span>
                          </div>
                          {currentLanguage === lang.code && (
                            <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* User Menu */}
                <div className="relative">
                  <button
                    type="button"
                    className="flex items-center text-sm rounded-lg sm:rounded-xl hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 hover:scale-105 p-1.5 sm:p-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUserMenuOpen(!userMenuOpen);
                    }}
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      {user?.avatar ? (
                        <img className="h-8 w-8 sm:h-10 sm:w-10 rounded-full ring-2 ring-white shadow-soft" src={user.avatar} alt="" />
                      ) : (
                        <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gradient-primary flex items-center justify-center ring-2 ring-white shadow-soft">
                          <span className="text-white text-xs sm:text-sm font-bold">
                            {user?.employee?.firstName?.charAt(0) || user?.firstName?.charAt(0) || user?.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                      )}
                      <div className="hidden lg:block text-left">
                        <p className="text-sm font-semibold text-secondary-900">
                          {user?.employee?.firstName && user?.employee?.lastName
                            ? `${user.employee.firstName} ${user.employee.lastName}`
                            : user?.firstName && user?.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user?.name || 'User'}
                        </p>
                        <p className="text-xs text-secondary-500">{user?.role || 'User'}</p>
                      </div>
                      <ChevronDownIcon className="h-3 w-3 sm:h-4 sm:w-4 text-secondary-500" />
                    </div>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-3 w-64 sm:w-72 bg-white rounded-2xl shadow-hard ring-1 ring-secondary-900/5 py-2 z-50 animate-fade-in-up">
                      <div className="px-4 sm:px-6 py-4 border-b border-secondary-100">
                        <div className="flex items-center space-x-3">
                          {user?.avatar ? (
                            <img className="h-10 w-10 sm:h-12 sm:w-12 rounded-full" src={user.avatar} alt="" />
                          ) : (
                            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-primary flex items-center justify-center">
                              <span className="text-white text-base sm:text-lg font-bold">
                                {user?.employee?.firstName?.charAt(0) || user?.firstName?.charAt(0) || user?.name?.charAt(0) || 'U'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-semibold text-secondary-900">
                              {user?.employee?.firstName && user?.employee?.lastName 
                                ? `${user.employee.firstName} ${user.employee.lastName}`
                                : user?.name || 'User'}
                            </p>
                            <p className="text-sm text-secondary-500">{user?.employee?.email || user?.email || 'user@example.com'}</p>
                            <p className="text-xs text-secondary-400 mt-1">{user?.role || 'User'}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="py-2">
                        <button 
                          onClick={() => navigate('/profile')}
                          className="flex items-center w-full px-4 py-3 text-sm text-secondary-700 hover:bg-secondary-50 transition-all duration-200 hover:translate-x-1"
                        >
                          <UserCircleIcon className="mr-3 h-5 w-5 text-secondary-500" />
                          {t('profile')}
                        </button>
                        
                        {/* Show Settings only to company_admin and super_admin (hide for employee role) */}
                        {(user?.role === 'company_admin' || user?.role === 'super_admin' || user?.role === 'hr_admin') && (
                          <button  onClick={() => navigate('/settings')} className="flex items-center w-full px-4 py-3 text-sm text-secondary-700 hover:bg-secondary-50 transition-all duration-200 hover:translate-x-1">
                            <Cog6ToothIcon className="mr-3 h-5 w-5 text-secondary-500" />
                            {t('settings')}
                          </button>
                        )}
                      </div>
                      
                      <div className="border-t border-secondary-100 pt-2">
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-3 text-sm text-error-600 hover:bg-error-50 transition-all duration-200 hover:translate-x-1"
                        >
                          <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                          {t('logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="pt-4 sm:pt-6 pb-6 px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

  {/* Mobile sidebar overlay handled inside Sidebar component to avoid z-index conflicts */}
    </div>
  );
};

export default Layout;
